import { QuestionCircleOutlined } from "@ant-design/icons";
import { Button, Layout as AntLayout } from "antd";
import { ReactNode } from "react";

import { JobHeader } from "./job-header";
import stylesheet from "./style.module.css";

import { Box } from "~/src/components/box";
import { Brand } from "~/src/components/brand";
import { Container } from "~/src/components/container";
import { Flex } from "~/src/components/flex";
import { HelpLink } from "~/src/components/help-link";
import { Id } from "~/src/lib/api/id";
import { useRouterScrollToTop } from "~/src/lib/use-router-scroll-to-top";
import { useSetFullHeight } from "~/src/lib/use-set-full-height";

type LayoutProps = {
  children?: ReactNode;
  footerContent?: ReactNode;
  jobId?: Id;
};

export const Layout = ({ children, footerContent, jobId }: LayoutProps) => {
  const elementRef = useSetFullHeight<HTMLDivElement>();

  return (
    <div ref={elementRef}>
      <AntLayout className={stylesheet.layout}>
        <Header />
        <Content>
          <JobHeader jobId={jobId} />

          <Container width="md">
            <Box paddingVertical="xl" paddingHorizontal="lg">
              {children}
            </Box>
          </Container>
        </Content>
        <Footer>{footerContent}</Footer>
      </AntLayout>
    </div>
  );
};

type HeaderProps = {
  extra?: ReactNode;
};

export const Header = ({ extra }: HeaderProps) => {
  return (
    <AntLayout.Header className={stylesheet.header}>
      <Container width="md">
        <Box paddingHorizontal="lg">
          <Flex sparsed>
            <Brand href="/" />
            {extra}
          </Flex>
        </Box>
      </Container>
    </AntLayout.Header>
  );
};

export type ContentProps = {
  children?: ReactNode;
};

export const Content = ({ children }: ContentProps) => {
  const elementRef = useRouterScrollToTop<HTMLDivElement>();

  return (
    <AntLayout.Content ref={elementRef} className={stylesheet.content}>
      {children}
    </AntLayout.Content>
  );
};

type FooterProps = {
  children?: ReactNode;
};

export const Footer = ({ children }: FooterProps) => {
  return (
    <AntLayout.Footer className={stylesheet.footer}>
      <Container>
        <Box paddingHorizontal="lg">
          <Flex sparsed gap="sm">
            <HelpLink passHref>
              <Button size="large" icon={<QuestionCircleOutlined />} />
            </HelpLink>
            {children}
          </Flex>
        </Box>
      </Container>
    </AntLayout.Footer>
  );
};

type SubheaderProps = {
  children: ReactNode;
};

export const Subheader = ({ children }: SubheaderProps) => {
  return (
    <AntLayout.Header className={stylesheet.subheader}>
      <Container width="md">
        <Box paddingVertical="sm" paddingHorizontal="lg">
          {children}
        </Box>
      </Container>
    </AntLayout.Header>
  );
};
